import React from 'react';
import Icon from '../../../components/AppIcon';

const AchievementsBadges = ({
  achievements = [
    {
      id: 1,
      title: 'First Steps',
      description: 'Completed your first chapter',
      icon: 'Play',
      color: 'success',
      earned: true,
      earnedDate: '2024-01-10',
      rarity: 'common'
    },
    {
      id: 2,
      title: 'Quick Learner',
      description: 'Completed a chapter in under 1 hour',
      icon: 'Zap',
      color: 'warning',
      earned: true,
      earnedDate: '2024-01-10',
      rarity: 'uncommon'
    },
    {
      id: 3,
      title: 'Perfect Score',
      description: 'Achieved 100% on a quiz',
      icon: 'Target',
      color: 'success',
      earned: false,
      earnedDate: null,
      rarity: 'rare'
    },

    {
      id: 5,
      title: 'Knowledge Seeker',
      description: 'Completed all presentations',
      icon: 'BookOpen',
      color: 'secondary',
      earned: false,
      earnedDate: null,
      rarity: 'rare'
    },
    {
      id: 6,
      title: 'AI Expert',
      description: 'Completed all chapters with 90%+ scores',
      icon: 'Trophy',
      color: 'success',
      earned: false,
      earnedDate: null,
      rarity: 'legendary'
    }
  ],
  certificates = [
    {
      id: 1,
      title: 'AI Fundamentals Certificate',
      description: 'Complete all chapters with passing scores',
      progress: 67,
      requirements: [
        { text: 'Complete Chapter 1', completed: true },
        { text: 'Complete Chapter 2', completed: true },
        { text: 'Complete Chapter 3', completed: false }
      ],
      available: false
    }
  ]
}) => {
  const earnedAchievements = achievements.filter(a => a.earned);
  const totalAchievements = achievements.length;

  const getRarityColor = (rarity) => {
    switch (rarity) {
      case 'common':
        return 'text-text-secondary';
      case 'uncommon':
        return 'text-secondary';
      case 'rare':
        return 'text-warning';
      case 'legendary':
        return 'text-accent';
      default:
        return 'text-text-secondary';
    }
  };

  const getRarityBg = (rarity) => {
    switch (rarity) {
      case 'common':
        return 'bg-surface-secondary';
      case 'uncommon':
        return 'bg-secondary-50';
      case 'rare':
        return 'bg-warning-50';
      case 'legendary':
        return 'bg-accent-50';
      default:
        return 'bg-surface-secondary';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  return (
    <div className="space-y-6">
      {/* Achievements Header */}
      <div className="p-6 border bg-surface rounded-xl border-border card-shadow">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-xl font-semibold font-heading text-text-primary">
              Achievements & Badges
            </h3>
            <p className="mt-1 text-text-secondary">
              Celebrate your learning milestones and accomplishments
            </p>
          </div>
          <div className="flex items-center px-3 py-2 space-x-2 rounded-lg bg-accent-50">
            <Icon name="Award" size={16} color="var(--color-accent)" strokeWidth={2} />
            <span className="text-sm font-medium text-accent-700">
              {earnedAchievements.length}/{totalAchievements} Earned
            </span>
          </div>
        </div>

        {/* Progress Bar */}
        <div className="mb-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-text-secondary">Achievement Progress</span>
            <span className="text-sm font-medium text-text-primary">
              {Math.round((earnedAchievements.length / totalAchievements) * 100)}%
            </span>
          </div>
          <div className="w-full h-2 rounded-full bg-border-light">
            <div
              className="h-2 rounded-full bg-gradient-to-r from-accent to-accent-600 progress-animation"
              style={{ width: `${(earnedAchievements.length / totalAchievements) * 100}%` }}
            />
          </div>
        </div>
      </div>

      {/* Achievements Grid */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {achievements.map((achievement) => (
          <div
            key={achievement.id}
            className={`
              relative bg-surface rounded-xl border transition-all duration-200 ease-out p-6
              ${achievement.earned
                ? 'border-success-200 hover:border-success-300 hover:shadow-md'
                : 'border-border opacity-60 hover:opacity-80'}
            `}
          >
            {/* Rarity Badge */}
            <div className={`
              absolute top-3 right-3 px-2 py-1 rounded-full text-xs font-medium capitalize
              ${getRarityBg(achievement.rarity)} ${getRarityColor(achievement.rarity)}
            `}>
              {achievement.rarity}
            </div>

            {/* Achievement Icon */}
            <div className={`
              flex items-center justify-center w-16 h-16 rounded-full mb-4 mx-auto
              ${achievement.earned
                ? `bg-${achievement.color}-50`
                : 'bg-surface-secondary'}
            `}>
              <Icon
                name={achievement.icon}
                size={24}
                color={achievement.earned ? `var(--color-${achievement.color})` : 'var(--color-text-muted)'}
                strokeWidth={2}
              />
            </div>

            {/* Achievement Details */}
            <div className="text-center">
              <h4 className={`
                text-lg font-heading font-semibold mb-2
                ${achievement.earned ? 'text-text-primary' : 'text-text-muted'}
              `}>
                {achievement.title}
              </h4>
              <p className={`
                text-sm mb-3
                ${achievement.earned ? 'text-text-secondary' : 'text-text-muted'}
              `}>
                {achievement.description}
              </p>

              {/* Earned Status */}
              {achievement.earned ? (
                <div className="flex items-center justify-center px-3 py-2 space-x-2 rounded-lg bg-success-50">
                  <Icon name="CheckCircle2" size={14} color="var(--color-success)" strokeWidth={2} />
                  <span className="text-xs font-medium text-success-700">
                    Earned {formatDate(achievement.earnedDate)}
                  </span>
                </div>
              ) : (
                <div className="flex items-center justify-center px-3 py-2 space-x-2 rounded-lg bg-surface-secondary">
                  <Icon name="Lock" size={14} color="var(--color-text-muted)" strokeWidth={2} />
                  <span className="text-xs font-medium text-text-muted">
                    Not Earned Yet
                  </span>
                </div>
              )}
            </div>

            {/* Earned Overlay Effect */}
            {achievement.earned && (
              <div className="absolute inset-0 pointer-events-none bg-gradient-to-br from-transparent to-success-50 opacity-20 rounded-xl" />
            )}
          </div>
        ))}
      </div>

      {/* Certificates Section */}
      <div className="p-6 border bg-surface rounded-xl border-border card-shadow">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h4 className="text-lg font-semibold font-heading text-text-primary">
              Certificates
            </h4>
            <p className="mt-1 text-text-secondary">
              Earn certificates by completing course requirements
            </p>
          </div>
          <div className="flex items-center px-3 py-2 space-x-2 rounded-lg bg-primary-50">
            <Icon name="FileText" size={16} color="var(--color-primary)" strokeWidth={2} />
            <span className="text-sm font-medium text-primary">
              {certificates.filter(c => c.available).length} Available
            </span>
          </div>
        </div>

        <div className="space-y-4">
          {certificates.map((certificate) => (
            <div
              key={certificate.id}
              className="p-6 transition-colors duration-200 border rounded-lg border-border hover:border-border-focus"
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-start space-x-3">
                  <div className="flex items-center justify-center w-12 h-12 rounded-lg bg-primary-50">
                    <Icon name="Award" size={20} color="var(--color-primary)" strokeWidth={2} />
                  </div>
                  <div>
                    <h5 className="mb-1 text-lg font-semibold font-heading text-text-primary">
                      {certificate.title}
                    </h5>
                    <p className="text-sm text-text-secondary">
                      {certificate.description}
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <div className="mb-1 text-lg font-bold font-heading text-primary">
                    {certificate.progress}%
                  </div>
                  <div className="text-xs text-text-secondary">Complete</div>
                </div>
              </div>

              {/* Progress Bar */}
              <div className="mb-4">
                <div className="w-full h-2 rounded-full bg-border-light">
                  <div
                    className="h-2 rounded-full bg-gradient-to-r from-primary to-primary-600 progress-animation"
                    style={{ width: `${certificate.progress}%` }}
                  />
                </div>
              </div>

              {/* Requirements */}
              <div className="space-y-2">
                <h6 className="text-sm font-medium text-text-primary">Requirements:</h6>
                {certificate.requirements.map((req, index) => (
                  <div key={index} className="flex items-center space-x-2">
                    <Icon
                      name={req.completed ? "CheckCircle2" : "Circle"}
                      size={16}
                      color={req.completed ? "var(--color-success)" : "var(--color-text-muted)"}
                      strokeWidth={2}
                    />
                    <span className={`text-sm ${req.completed ? 'text-text-primary' : 'text-text-muted'}`}>
                      {req.text}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default AchievementsBadges;